import ReactQuill from "react-quill";
import Quill from "quill";
import {
  beautifyHtml,
  minifyHtml,
  removeSpacesBetweenTags,
} from "utilz/helpers/stringManipulator";
import { debugConsole } from "utilz/helpers";

const Delta = Quill.import("delta");
const Embed = Quill.import("blots/embed");
const Image = Quill.import("formats/image");
const Block = Quill.import("blots/block");

class ChipBlot extends Embed {
  static blotName = "chip";
  static tagName = "span";
  static className = "dd-template-chip";

  static create(value: {
    value?: string;
    label?: string;
    id?: string;
    bgColor?: string;
    borderColor?: string;
    fontColor?: string;
    icon?: string;
  }) {
    // Handle cases where value might be malformed or not an object
    if (!value || typeof value !== "object") {
      // Create a fallback text node if value is invalid
      const node = document.createElement("span");
      node.textContent = typeof value === "string" ? value : "";
      return node;
    }
    let chipContentHeight = "24px";
    let chipContentPadding = "0 6px";
    if (value.label == "Activate Account" && value.id == "redirect_button") {
      chipContentHeight = "34px";
      chipContentPadding = "0 16px";
    }

    // Create the base node
    const node = super.create();
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-chip", "true");
    node.setAttribute("data-value", value.value || "");
    node.setAttribute("data-label", value.label || "");
    node.setAttribute("data-id", value.id || "");
    node.setAttribute("id", value.id || "");
    node.className = this.className;

    // Style the node
    node.style.display = "inline-block";
    node.style.margin = "0 2px";
    // node.style.verticalAlign = "middle"; // commenting because of in mail alignement varying with chips

    // Create the chip content
    const chipContent = document.createElement("span");
    chipContent.style.display = "inline-flex";
    chipContent.style.alignItems = "center";
    chipContent.style.height = chipContentHeight || "24px";
    chipContent.style.gap = "8px";
    // chipContent.style.border = `1px solid ${
    //   value.borderColor || "rgba(234, 214, 255, 1)"
    // }`;
    chipContent.style.border = `1px solid ${"rgba(214, 220, 255, 1)"}`;
    chipContent.style.borderRadius = "4px";
    chipContent.style.padding = chipContentPadding || "0 6px";
    // chipContent.style.backgroundColor =
    //   value.bgColor || "rgba(241, 232, 255, 1)";
    // chipContent.style.backgroundColor = "rgba(234, 237, 255, 1)";
    chipContent.style.backgroundColor =
      value.bgColor || "rgba(234, 237, 255, 1)";
    chipContent.style.cursor = "default";
    chipContent.style.whiteSpace = "nowrap";

    // Add icon if provided
    if (value.icon) {
      const iconSpan = document.createElement("span");
      iconSpan.className = "chip-icon";
      iconSpan.innerHTML = value.icon;
      chipContent.appendChild(iconSpan);
    }

    // Add label text
    const textSpan = document.createElement("span");
    textSpan.className = "chip-text";
    textSpan.style.fontSize = "12px";
    textSpan.style.verticalAlign = "middle";
    textSpan.style.color = value.fontColor || "rgba(60, 11, 193, 1)";
    // textSpan.style.color = "rgba(60, 11, 193, 1)";
    textSpan.style.display = "inline-flex";
    textSpan.style.alignItems = "center";
    // textSpan.style.height = "24px";
    textSpan.textContent = value.label || "";
    chipContent.appendChild(textSpan);

    node.appendChild(chipContent);
    return node;
  }

  static value(node: HTMLElement) {
    // Try to get values from attributes first
    const value = node.getAttribute("data-value") || "";
    const label =
      node.getAttribute("data-label") ||
      node.querySelector(".chip-text")?.textContent ||
      "";
    const id = node.getAttribute("id") || "";

    // Get colors from the chip content
    const chipContent = node.querySelector("span") || node;
    const chipContentBgcolor =
      node.querySelector("span[style*='background-color']") || node;
    const chipContentFontcolor: any = node.querySelector("span.chip-text");
    let bgColorChip = "rgba(234, 237, 255, 1)";
    let fontColorChip = "rgba(60, 11, 193, 1)";
    try {
      bgColorChip = window.getComputedStyle(chipContentBgcolor).backgroundColor;
      fontColorChip = window.getComputedStyle(chipContentFontcolor).color;
    } catch (e) {
      debugConsole("Blot component error", e);
    }
    const bgColor = bgColorChip;
    const fontColor = fontColorChip;
    const borderColor =
      chipContent.style.borderColor || "rgba(234, 214, 255, 1)";
    // const fontColor = "rgba(102, 0, 250, 1)";

    // Fallback to parsing from HTML if attributes are missing
    if (!value || !label || !id) {
      const textContent =
        node.querySelector(".chip-text")?.textContent || node.textContent || "";
      return {
        value: textContent,
        label: textContent,
        id: "",
        bgColor,
        borderColor,
        fontColor,
      };
    }

    return {
      value,
      label,
      id,
      bgColor,
      borderColor,
      fontColor,
    };
  }
}

class StyledImageBlot extends Image {
  static create(value: { src: string; alt: string; style: string }) {
    const node = super.create(value);

    // If value is an object with properties
    if (typeof value === "object") {
      node.setAttribute("src", value.src);
      node.setAttribute("alt", value.alt || "Uploaded Image");
      node.setAttribute(
        "style",
        value.style || "max-width: 100px; height: auto;"
      );
      // Outlook desktop ignores CSS max-width but honours the width attribute.
      // Only mirror an explicit `width:Npx` -- deriving one from `max-width`
      // would UPSCALE any image smaller than that cap.
      const widthMatch = /(?:^|;)\s*width\s*:\s*(\d+)px/.exec(
        node.getAttribute("style") || ""
      );
      if (widthMatch) {
        node.setAttribute("width", widthMatch[1]);
      }
    }

    return node;
  }

  static value(node: {
    getAttribute: (arg0: string) => any;
    querySelector: (selector: string) => any;
    className: string;
  }) {
    return {
      src: node.getAttribute("src"),
      alt: node.getAttribute("alt"),
      style: node.getAttribute("style"),
    };
  }
}

class SignatureBlot extends Embed {
  static blotName = "signature";
  static tagName = "div";

  static create(value: { content: string; id?: string }) {
    const node = super.create(value);
    node.setAttribute("contenteditable", "true"); // Make it non-editable
    node.setAttribute("data-signature", "true");
    node.setAttribute("data-id", value.id || `sig-${Date.now()}`); // Unique ID

    // Style the signature container
    node.style.border = "1px solid #E0E0E0";
    node.style.backgroundColor = "#f9f9f9";
    // node.style.margin = "4px 0";
    node.style.display = "block";

    // Insert the HTML content
    node.innerHTML = value.content;
    // node.innerHTML = "<p contenteditable='false'>eerrerererer</p>";

    return node;
  }
  static value(node: HTMLElement) {
    return {
      content: node.innerHTML,
      id: node.getAttribute("data-id") || "",
    };
  }
}

class HTMLCodeBlot extends Embed {
  static blotName = "htmlCode";
  static tagName = "div";
  static className = "html-code-blot";

  static create(value: { code: string; id?: string }) {
    const node = super.create(value);
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-html-code", "true");
    node.setAttribute("data-id", value.id || `html-${Date.now()}`);
    // Store the HTML code in the data-code attribute
    node.setAttribute("data-code", value.code || "");
    node.className = this.className;
    node.style.borderRadius = "4px";
    node.style.padding = "8px";
    node.style.margin = "4px 0";
    // node.style.position = "relative";
    node.style.cursor = "pointer"; // Add cursor pointer to indicate clickable
    // node.style.height = "fit-content";
    // node.style.width = "fit-content";
    node.style.minWidth = "100%";

    const rawHtmlContent = value.code || node.getAttribute("data-code") || "";

    // Sanitize HTML content before setting (similar to React's dangerouslySetInnerHTML intent)

    const sanitizedHtmlContent = minifyHtml(rawHtmlContent);
    const minifiedHtmlContent = removeSpacesBetweenTags(sanitizedHtmlContent);

    // Add click event listener to the node
    node.addEventListener("click", (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      // Create and dispatch custom event
      const customEvent = new CustomEvent("htmlCodeEdit", {
        detail: {
          id: node.getAttribute("data-id"),
          code: rawHtmlContent,
        },
        bubbles: true,
      });
      node.dispatchEvent(customEvent);
    });

    // // Set the sanitized content to the node
    this.setSecureInnerHTML(node, minifiedHtmlContent);

    return node;
  }

  // Security-conscious method for setting HTML content (similar to dangerouslySetInnerHTML intent)
  private static setSecureInnerHTML(
    element: HTMLElement,
    htmlContent: string
  ): void {
    element.innerHTML = htmlContent;
  }

  static value(node: HTMLElement) {
    return {
      code: node.getAttribute("data-code") || "",
      id: node.getAttribute("data-id") || "",
    };
  }
}

export { ChipBlot, StyledImageBlot, SignatureBlot, HTMLCodeBlot };
